// eslint-disable-next-line max-classes-per-file
import isArray from 'lodash/isArray';
import isEqual from 'lodash/isEqual';

export class NamespacedCacheEntry {
  constructor(key, value) {
    this.key = key;
    this.value = value;
  }

  keyMatch(key) {
    return isEqual(this.key, key);
  }

  keyContain(key) {
    if (isArray(key)) {
      // if key is array check if it is sub-array
      return isEqual(this.key.slice(0, key.length), key);
    }
    // otherwise run strict check
    return this.keyMatch(key);
  }
}

export class NamespacedCacheEntriesCollection {
  constructor(entries = []) {
    this.entries = entries;
  }

  get size() {
    return this.entries.length;
  }

  getEntryByKey(key) {
    return this.entries.find((entry) => entry.keyMatch(key));
  }

  addEntryByKey(key, value) {
    const entry = new NamespacedCacheEntry(key, value);
    this.entries.unshift(entry);
    return entry;
  }

  removeEntriesByKey(key) {
    this.entries = this.entries.filter((entry) => !entry.keyContain(key));
  }

  limitEntries(size) {
    this.entries = this.entries.slice(0, size);
  }
}

/*
 * Custom namespaced cache to store values under key.
 * If key is array - it works as namespace path.
 * If key is not an array - it works as generic cache and doesn't support namespacing.
 *
 * Usage examples:
 * namespacedCache.setByKey(['ns1', 'ns2', { param1: 1 }], 'some value');
 * namespacedCache.getByKey(['ns1', 'ns2', { param1: 1 }]); // 'some value'
 * namespacedCache.getByKey(['ns1', 'ns2', { param2: 2 }]); // undefined
 * namespacedCache.getByKey(['ns1']); // undefined, it is not allowed to get everything from namespace, only by exactly the same key it was set
 * namespacedCache.removeByKey(['ns1']); // will remove everything under ['ns1'] namespace
 */
export class NamespacedCache {
  constructor(maxEntries) {
    this.maxEntries = maxEntries;
    this.entriesCollection = new NamespacedCacheEntriesCollection();
  }

  _checkCacheSize() {
    if (this.entriesCollection.size > this.maxEntries) {
      this.entriesCollection.limitEntries(this.maxEntries);
    }
  }

  getByKey(key) {
    const entry = this.entriesCollection.getEntryByKey(key);
    return entry && entry.value;
  }

  setByKey(key, value) {
    this.entriesCollection.removeEntriesByKey(key); // remove if it already exists
    this.entriesCollection.addEntryByKey(key, value);
    this._checkCacheSize();
  }

  removeByKey(key) {
    this.entriesCollection.removeEntriesByKey(key);
  }
}

export default new NamespacedCache(20);
